import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  givenHealthCareProfessionalAdviceSchema,
  IncidentTypeSchema,
  longTextSchema,
  nhsContactResults,
  nhsContactSchema,
  shortTextSchema,
  triageOutcomeSchema,
  voluntaryActionSchema,
  yesNoSchema,
} from "../../types/index.js";
import { tk, type TranslationKey } from "../../types/Translation.js";

export const reasonNotConsultedSchema = z.enum([
  "violent",
  "immediateRestraint",
  "singleCrewed",
  "poorConnectivity",
  "noReply",
]);

export const formSpecification = new MultiPageFormBuilder()
  // Page 1: Initial Consultation Check
  .withFieldAlwaysVisible({
    fieldName: "isHealthcareProfessionalConsulted", // triage-1.1
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("triageIncident.isHealthcareProfessionalConsulted"),
    requiredLabel: tk(
      "triageIncident.isHealthcareProfessionalConsultedRequired",
    ),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  // Page 2: Consultation Details or Reason Not Consulted
  .withFieldConditionallyVisible({
    fieldName: "howDidYouContactNhs", // triage-2.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(nhsContactSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "yes",
    label: tk("triageIncident.howDidYouContactNhs.title"),
    requiredLabel: tk("triageIncident.howDidYouContactNhs.required"),
    options: [
      {
        value: "healthcarePractitionerAmbulance", // triage-2.1.1
        label: tk(
          "triageIncident.howDidYouContactNhs.options.healthcarePractitionerAmbulance",
        ),
      },
      {
        value: "ldPractitionersInCustody", // triage-2.1.2
        label: tk(
          "triageIncident.howDidYouContactNhs.options.ldPractitionersInCustody",
        ),
      },
      {
        value: "custodyHealthcarePractitioner", // triage-2.1.2
        label: tk(
          "triageIncident.howDidYouContactNhs.options.custodyHealthcarePractitioner",
        ),
      },

      {
        value: "panLondonMentalHealthAdviceLine",
        label: tk(
          "triageIncident.howDidYouContactNhs.options.panLondonMentalHealthAdviceLine",
        ),
      },

      {
        value: "localMentalHealthAdviceLine",
        label: tk(
          "triageIncident.howDidYouContactNhs.options.localMentalHealthAdviceLine",
        ),
      },

      {
        value: "localHbpos",
        label: tk("triageIncident.howDidYouContactNhs.options.localHbpos"),
      },

      {
        value: "localStreetTriageTeam",
        label: tk(
          "triageIncident.howDidYouContactNhs.options.localStreetTriageTeam",
        ),
      },
    ] satisfies Array<{
      value: (typeof nhsContactResults)[number];
      label: TranslationKey;
    }>,
  })
  .withFieldConditionallyVisible({
    fieldName: "howDidYouContactNhsOtherDetails", // triage-2.1.13.1
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.howDidYouContactNhs?.value === "other",
    label: tk("triageIncident.completeReport"),
  })
  .withFieldConditionallyVisible({
    fieldName: "reasonNotConsulted", // triage-2.2
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(reasonNotConsultedSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "no",
    label: tk("triageIncident.reasonNotConsulted.title"),
    requiredLabel: tk("triageIncident.reasonNotConsulted.required"),
    options: [
      {
        label: tk("triageIncident.reasonNotConsulted.options.violent"),
        value: "violent",
      },
      {
        label: tk(
          "triageIncident.reasonNotConsulted.options.immediateRestraint",
        ),
        value: "immediateRestraint",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.singleCrewed"),
        value: "singleCrewed",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.poorConnectivity"),
        value: "poorConnectivity",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.noReply"),
        value: "noReply",
      },
    ],
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "reasonNotConsultedBanner", // triage-2.3
    type: "banner",
    body: tk("triageIncident.reasonNotConsulted.banner"),
    bannerType: "info",
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "no",
  })
  // Page 3: Healthcare Professional Advice
  .withFieldConditionallyVisible({
    fieldName: "givenHealthCareProfessionalAdvice", // triage-3.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(givenHealthCareProfessionalAdviceSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "yes",
    label: tk("triageIncident.givenHealthCareProfessionalAdvice"),
    requiredLabel: tk(
      "triageIncident.givenHealthCareProfessionalAdviceRequired",
    ),
    options: [
      {
        label: tk("triageIncident.whatWillYouDo.136"),
        value: IncidentTypeSchema.enum.section136,
      },
      {
        label: tk("triageIncident.whatWillYouDo.56"),
        value: IncidentTypeSchema.enum.mentalCapacityAct,
      },
      {
        label: tk("triageIncident.whatWillYouDo.voluntary"),
        value: IncidentTypeSchema.enum.voluntary,
      },
      {
        label: tk("triageIncident.whatWillYouDo.other"),
        value: IncidentTypeSchema.enum.other,
      },
    ],
  })

  .withFieldNeverVisible({
    fieldName: "reasonsForDetention", // triage-1.0.4.1.1
    ...new FieldBuilder().withNullableSchema(longTextSchema).build(),
    defaultValue: () => {
      return null;
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "voluntaryAction", // triage-3.1.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(voluntaryActionSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.givenHealthCareProfessionalAdvice?.value ===
      IncidentTypeSchema.enum.voluntary,
    label: tk("triageIncident.voluntaryServicesRecommended"),
    requiredLabel: tk("triageIncident.voluntaryServicesRecommendedRequired"),
    options: [
      {
        label: tk(
          "triageIncident.voluntaryAction.options.divertOtherHealthservice",
        ),
        value: "divertOtherHealthservice",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.contactSupportGroup"),
        value: "contactSupportGroup",
      },
      {
        label: tk(
          "triageIncident.voluntaryAction.options.contactFriendsFamily",
        ),
        value: "contactFriendsFamily",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.takePersonHome"),
        value: "takePersonHome",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.divertCrisisCafe"),
        value: "divertCrisisCafe",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "voluntaryActionOtherDetails", // triage-3.1.3.1
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.voluntaryAction?.value === "other",
    label: tk("triageIncident.voluntaryAction.other.title"),
    requiredLabel: tk("triageIncident.voluntaryAction.other.required"),
    multiline: true,
    rows: "5",
  })
  // Page 4: Final Action
  .withFieldAlwaysVisible({
    fieldName: "whatWillYouDo", // triage-4.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(triageOutcomeSchema)
      .captureValueLabel()
      .build(),
    label: tk("triageIncident.whatActionWillYouTake"),
    requiredLabel: tk("triageIncident.whatActionWillYouTakeRequired"),
    options: ({ user }) => [
      {
        label: tk("triageIncident.whatWillYouDo.136"),
        value: IncidentTypeSchema.enum.section136,
      },
      ...(user?.sessionOrganisationConfiguration?.mentalCapacityActEnabled
        ? [
            {
              label: tk("triageIncident.whatWillYouDo.mentalCapacityAct"),
              value: IncidentTypeSchema.enum.mentalCapacityAct,
            } as const,
          ]
        : []),
      {
        label: tk("triageIncident.whatWillYouDo.alternativePathway"),
        value: IncidentTypeSchema.enum.voluntary,
      },
      {
        label: tk("triageIncident.whatWillYouDo.arrestForCriminalOffence"),
        value: IncidentTypeSchema.enum.other,
      },
    ],
    isEditable: ({ initialValue }) => {
      return initialValue === null;
    },
  })
  .withFieldNeverVisible({
    ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
    fieldName: "whatWillYouDoOther",
    defaultValue: () => {
      return null;
    },
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "unsupportedAction",
    type: "banner",
    body: tk("triageIncident.unsupportedAction"),
    bannerType: "error",
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(
          IncidentTypeSchema.enum.voluntary,
          IncidentTypeSchema.enum.other,
          () => true,
        )
        .otherwise(() => false),
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "s136ReportBanner", // triage-4.4
    type: "banner",
    body: tk("triageIncident.s136MentalHealthInvestigationBanner"),
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(IncidentTypeSchema.enum.section136, () => true)
        .otherwise(() => false),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "mca2005ReportBanner", // triage-4.5
    type: "banner",
    body: tk("triageIncident.useOfMCA2005InvestigationBanner"), // new translation key
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(IncidentTypeSchema.enum.mentalCapacityAct, () => true)
        .otherwise(() => false),
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "criminalOffenceWarningBanner", // triage-4.3
    type: "banner",
    body: tk("triageIncident.criminalOffenceWarningBanner"),
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(undefined, () => false)
        .with(IncidentTypeSchema.enum.section136, () => false)
        .with(IncidentTypeSchema.enum.mentalCapacityAct, () => false)
        .with(IncidentTypeSchema.enum.other, () => true)
        .with(IncidentTypeSchema.enum.voluntary, () => false)
        .exhaustive(),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "alternativePathwayReportWarningBanner", // triage-4.3
    type: "banner",
    body: tk("triageIncident.completeReport"),
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(undefined, () => false)
        .with(IncidentTypeSchema.enum.section136, () => false)
        .with(IncidentTypeSchema.enum.mentalCapacityAct, () => false)
        .with(IncidentTypeSchema.enum.other, () => false)
        .with(IncidentTypeSchema.enum.voluntary, () => true)
        .exhaustive(),
  })
  .withPage([
    "isHealthcareProfessionalConsulted", // triage-1.1
  ])
  .withPage([
    "reasonNotConsulted", // triage-2.2
    "reasonNotConsultedBanner", // triage-2.3
    "howDidYouContactNhs", // triage-2.1
    "howDidYouContactNhsOtherDetails", // triage-2.1.13.1
  ])
  .withPage([
    "givenHealthCareProfessionalAdvice", // triage-3.1
    "voluntaryAction", // triage-3.1.3
    "voluntaryActionOtherDetails", // triage-3.1.3.1
  ])
  .withPage([
    "whatWillYouDo", // triage-4.1
    "criminalOffenceWarningBanner", // triage-4.2
    "alternativePathwayReportWarningBanner", // triage-4.2
    "s136ReportBanner", // triage-4.4
    "mca2005ReportBanner", // triage-4.5
  ])
  .withPage(["unsupportedAction"]) // triage-5.0
  .build({ t });

export type TriageIncidentFormData = ReturnType<
  typeof formSpecification
>["formType"];
